/**
 * Pure helpers for the Objects page taxonomy tree (built by `buildObjectTree` in
 * `@utils/taxonomyTree`): the search filter and the "enough annotations to train"
 * gate.
 */

/** Annotated examples an object needs before a child object can be trained from it. */
export const MIN_ANNOTATIONS = 150;

/** Whether a node is a Biodrone-maintained (read-only) system object. */
export const isSystemNode = node => node?.scope === 'system';

/**
 * The aliases the object card shows — only system nodes display theirs. Search
 * matches these same aliases, so a result always shows why it matched.
 */
export const displayedAliases = node => (isSystemNode(node) ? (node.aliases ?? []) : []);

/**
 * Whether the annotation summary reported a count for `node`. Until it loads
 * (or for a node it does not list) the card shows a "–/150" placeholder.
 */
export const hasAnnotationCount = node => typeof node?.annotations === 'number';

/** The threshold `node` must reach: the summary's per-node value, else the default. */
export const minAnnotationsOf = node => node?.min_annotations ?? MIN_ANNOTATIONS;

/**
 * Whether a child object can be trained from `node`: enough annotated examples.
 * Deliberately independent of `is_available`, which only says whether a model
 * is bound for the selected project type — the details gate uses the same rule.
 */
export const canTrainChild = node => (node?.annotations ?? 0) >= minAnnotationsOf(node);

/**
 * Attach `GET /v2/annotations/summary` to the taxonomy nodes it describes.
 * A node the summary does not mention is returned untouched, so its card keeps
 * the placeholder rather than showing a false zero.
 *
 * Adds: `annotations`, `min_annotations`, `annotation_labels`
 * (`[{ label, annotation_count }]`, `label` null for unlabelled) and
 * `annotation_projects` (`[{ project_uuid, annotation_count }]`).
 */
export const mergeAnnotationSummary = (nodes = [], summary = []) => {
	if (!summary.length) return nodes;
	const byUuid = new Map(summary.map(entry => [entry.node_uuid, entry]));
	return nodes.map(node => {
		const entry = byUuid.get(node.uuid);
		if (!entry) return node;
		return {
			...node,
			annotations: entry.annotation_count ?? 0,
			min_annotations: entry.min_required_count ?? MIN_ANNOTATIONS,
			annotation_labels: entry.labels ?? [],
			annotation_projects: entry.projects ?? [],
		};
	});
};

const normalize = value => value.toLowerCase().replace(/_/g, ' ');

const matchesQuery = (node, query) =>
	normalize(node.name ?? '').includes(query) ||
	displayedAliases(node).some(alias => normalize(alias).includes(query));

/**
 * Filter the tree to objects whose name (or, on a system node, an alias) matches `query`, keeping
 * each match's ancestors (so it stays in context) and all of its descendants.
 */
export const filterObjectTree = (tree = [], query = '') => {
	const normalizedQuery = normalize(query.trim());
	if (!normalizedQuery) return tree;

	const filter = nodes =>
		nodes.reduce((result, node) => {
			if (matchesQuery(node, normalizedQuery)) {
				result.push(node);
				return result;
			}
			const children = filter(node.children ?? []);
			if (children.length > 0) result.push({ ...node, children });
			return result;
		}, []);

	return filter(tree);
};

/** Every uuid in a (possibly filtered) tree, for expand-all. */
export const collectUuids = (tree = []) =>
	tree.flatMap(node => [node.uuid, ...collectUuids(node.children ?? [])]);
